import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import {
  type ExclusionRegion,
  type ContactAdjustment,
} from "./contactAnalysis";
import {
  IDENTITY_JAW,
  type JawTransform,
  jawTransformToMatrix,
} from "./meshMath";

export type { JawTransform };
export { IDENTITY_JAW, jawTransformToMatrix };

export interface OcclusionViewerMeshes {
  upper: THREE.BufferGeometry | null;
  lower: THREE.BufferGeometry | null;
  bite: THREE.BufferGeometry | null;
  showBite?: boolean;
}

export interface JawDragConfig {
  enabled: boolean;
  jaw: "upper" | "lower";
  transform: JawTransform;
  /** Multiplier for view-plane drag (mm per pixel scale). Lower = finer. */
  translateSensitivity?: number;
  /** Degrees per pixel when shift-dragging for tilt. */
  rotateSensitivity?: number;
}

export interface OcclusionViewerHandle {
  zoomOutOverview: () => void;
}

interface OcclusionViewerProps {
  meshes: OcclusionViewerMeshes;
  upperTransform?: JawTransform;
  lowerTransform?: JawTransform;
  jawDrag?: JawDragConfig | null;
  onJawDrag?: (transform: JawTransform) => void;
  onJawDragEnd?: (transform: JawTransform) => void;
  contactAdjustments?: ContactAdjustment[];
  selectedAdjustmentId?: number | null;
  showAllAdjustments?: boolean;
  movableJaw?: "upper" | "lower";
  excludeMode?: boolean;
  exclusionRegions?: ExclusionRegion[];
  onAddExclusion?: (center: { x: number; y: number; z: number }) => void;
  priorityPaintMode?: boolean;
  priorityRegions?: ExclusionRegion[];
  onAddPriority?: (center: { x: number; y: number; z: number }) => void;
  className?: string;
  caption?: string;
  captionClassName?: string;
}

const MARKER_COLORS = [0x22d3ee, 0x34d399, 0xfbbf24, 0xfb923c, 0xf472b6];

const AXIS_DIRECTION: Record<keyof JawTransform, THREE.Vector3> = {
  tx: new THREE.Vector3(1, 0, 0),
  ty: new THREE.Vector3(0, 1, 0),
  tz: new THREE.Vector3(0, 0, 1),
  rx: new THREE.Vector3(1, 0, 0),
  ry: new THREE.Vector3(0, 1, 0),
  rz: new THREE.Vector3(0, 0, 1),
};

const OcclusionViewer = forwardRef<OcclusionViewerHandle, OcclusionViewerProps>(
  (
    {
      meshes,
      upperTransform = IDENTITY_JAW,
      lowerTransform = IDENTITY_JAW,
      jawDrag = null,
      onJawDrag,
      onJawDragEnd,
      contactAdjustments = [],
      selectedAdjustmentId = null,
      showAllAdjustments = true,
      movableJaw = "lower",
      excludeMode = false,
      exclusionRegions = [],
      onAddExclusion,
      priorityPaintMode = false,
      priorityRegions = [],
      onAddPriority,
      className,
      caption,
      captionClassName,
    },
    ref
  ) => {
    const mountRef = useRef<HTMLDivElement>(null);
    const onAddExclusionRef = useRef(onAddExclusion);
    onAddExclusionRef.current = onAddExclusion;
    const onAddPriorityRef = useRef(onAddPriority);
    onAddPriorityRef.current = onAddPriority;
    const onJawDragRef = useRef(onJawDrag);
    onJawDragRef.current = onJawDrag;
    const onJawDragEndRef = useRef(onJawDragEnd);
    onJawDragEndRef.current = onJawDragEnd;
    const jawDragRef = useRef(jawDrag);
    jawDragRef.current = jawDrag;
    const apiRef = useRef<{
      setMeshes: (m: OcclusionViewerMeshes) => void;
      setJawTransforms: (upper: JawTransform, lower: JawTransform) => void;
      setContactAdjustments: (
        adjustments: ContactAdjustment[],
        selectedId: number | null,
        showAll: boolean,
        jaw: "upper" | "lower"
      ) => void;
      setExclusionRegions: (regions: ExclusionRegion[]) => void;
      setPriorityRegions: (regions: ExclusionRegion[]) => void;
      setExcludeMode: (active: boolean) => void;
      setPriorityPaintMode: (active: boolean) => void;
      setJawDragMode: (config: JawDragConfig | null) => void;
      zoomOutOverview: () => void;
      dispose: () => void;
    } | null>(null);

    useImperativeHandle(ref, () => ({
      zoomOutOverview: () => apiRef.current?.zoomOutOverview(),
    }));

    useEffect(() => {
      const mount = mountRef.current;
      if (!mount) return;

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x0a0f1a);
      scene.fog = new THREE.Fog(0x0a0f1a, 180, 900);

      const camera = new THREE.PerspectiveCamera(
        38,
        mount.clientWidth / Math.max(1, mount.clientHeight),
        0.05,
        10000
      );
      // Bite frame: +Z toward the upper arch, +Y anterior — view from the front.
      camera.up.set(0, 0, 1);
      camera.position.set(34, 82, 30);

      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
        powerPreference: "high-performance",
      });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
      renderer.setSize(mount.clientWidth, mount.clientHeight);
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.05;
      mount.appendChild(renderer.domElement);

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.07;
      controls.minDistance = 1.5;
      controls.maxDistance = 2500;
      controls.rotateSpeed = 0.85;

      const hemi = new THREE.HemisphereLight(0xf8fafc, 0x1e293b, 0.95);
      scene.add(hemi);
      const key = new THREE.DirectionalLight(0xffffff, 0.95);
      key.position.set(55, 45, 100);
      scene.add(key);
      const fill = new THREE.DirectionalLight(0x93c5fd, 0.4);
      fill.position.set(-60, -45, 35);
      scene.add(fill);
      const rim = new THREE.DirectionalLight(0xffffff, 0.25);
      rim.position.set(0, 60, -40);
      scene.add(rim);
      hemi.position.set(0, 0, 1);

      const grid = new THREE.GridHelper(140, 28, 0x2a3650, 0x152033);
      grid.rotation.x = Math.PI / 2;
      grid.position.z = -30;
      scene.add(grid);

      const root = new THREE.Group();
      scene.add(root);

      const upperGroup = new THREE.Group();
      const lowerGroup = new THREE.Group();
      const biteGroup = new THREE.Group();
      root.add(upperGroup, lowerGroup, biteGroup);

      const markersGroup = new THREE.Group();
      scene.add(markersGroup);

      const exclusionGroup = new THREE.Group();
      const priorityGroup = new THREE.Group();
      scene.add(exclusionGroup, priorityGroup);

      let upperMesh: THREE.Mesh | null = null;
      let lowerMesh: THREE.Mesh | null = null;
      let biteMesh: THREE.Mesh | null = null;
      let modelRadius = 40;
      /** Frame the camera only on the first meshes of a session, so recomputes keep the user's view. */
      let hasFramed = false;
      let modelCenter = new THREE.Vector3();

      const raycaster = new THREE.Raycaster();
      const pointer = new THREE.Vector2();
      let excludeModeActive = false;
      let priorityPaintActive = false;
      let jawDragActive = false;
      let paintHandler: ((e: PointerEvent) => void) | null = null;

      const paintModeActive = () => excludeModeActive || priorityPaintActive;

      const syncMouseButtons = () => {
        if (paintModeActive()) {
          controls.mouseButtons = {
            LEFT: null,
            MIDDLE: THREE.MOUSE.DOLLY,
            RIGHT: THREE.MOUSE.PAN,
          };
          renderer.domElement.style.cursor = "crosshair";
          return;
        }
        if (jawDragActive) {
          controls.mouseButtons = {
            LEFT: null,
            MIDDLE: THREE.MOUSE.DOLLY,
            RIGHT: THREE.MOUSE.ROTATE,
          };
          renderer.domElement.style.cursor = "grab";
          return;
        }
        controls.mouseButtons = {
          LEFT: THREE.MOUSE.ROTATE,
          MIDDLE: THREE.MOUSE.DOLLY,
          RIGHT: THREE.MOUSE.PAN,
        };
        renderer.domElement.style.cursor = "default";
      };

      const setExcludeMode = (active: boolean) => {
        excludeModeActive = active;
        if (active) priorityPaintActive = false;
        syncMouseButtons();
      };

      const setPriorityPaintMode = (active: boolean) => {
        priorityPaintActive = active;
        if (active) excludeModeActive = false;
        syncMouseButtons();
      };

      const setJawDragMode = (config: JawDragConfig | null) => {
        jawDragActive = Boolean(config?.enabled);
        syncMouseButtons();
      };

      const paintAtPointer = (event: PointerEvent) => {
        if (!paintModeActive() || !upperMesh) return;
        const rect = renderer.domElement.getBoundingClientRect();
        pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(pointer, camera);
        const hits = raycaster.intersectObject(upperMesh, false);
        if (!hits.length) return;
        const hit = hits[0].point;
        const pt = { x: hit.x, y: hit.y, z: hit.z };
        if (excludeModeActive) onAddExclusionRef.current?.(pt);
        else if (priorityPaintActive) onAddPriorityRef.current?.(pt);
      };

      const onPointerMove = (e: PointerEvent) => {
        if (!paintModeActive() || e.buttons !== 1) return;
        e.preventDefault();
        paintAtPointer(e);
      };

      paintHandler = (e: PointerEvent) => {
        if (!paintModeActive()) return;
        e.preventDefault();
        paintAtPointer(e);
      };
      renderer.domElement.addEventListener("pointerdown", paintHandler);
      renderer.domElement.addEventListener("pointermove", onPointerMove);

      let jawDragState: {
        startX: number;
        startY: number;
        startTransform: JawTransform;
        latestTransform: JawTransform;
        mode: "telescopic" | "tilt" | "roll";
      } | null = null;
      let wheelEndTimer: ReturnType<typeof setTimeout> | null = null;

      const getMovableMesh = () => {
        const cfg = jawDragRef.current;
        if (!cfg?.enabled) return null;
        return cfg.jaw === "upper" ? upperMesh : lowerMesh;
      };

      const pointerToNdc = (event: MouseEvent) => {
        const rect = renderer.domElement.getBoundingClientRect();
        pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      };

      const onJawPointerDown = (e: PointerEvent) => {
        const cfg = jawDragRef.current;
        if (!cfg?.enabled || paintModeActive() || e.button !== 0) return;

        const movable = getMovableMesh();
        if (!movable) return;

        pointerToNdc(e);
        raycaster.setFromCamera(pointer, camera);
        const hits = raycaster.intersectObject(movable, false);
        if (!hits.length) return;

        e.preventDefault();
        e.stopPropagation();
        controls.enabled = false;
        renderer.domElement.style.cursor = "grabbing";

        const startTransform = { ...cfg.transform };
        const mode: "telescopic" | "tilt" | "roll" = e.shiftKey
          ? "tilt"
          : e.altKey
            ? "roll"
            : "telescopic";
        jawDragState = {
          startX: e.clientX,
          startY: e.clientY,
          startTransform,
          latestTransform: startTransform,
          mode,
        };
        renderer.domElement.setPointerCapture(e.pointerId);
      };

      const onJawPointerMove = (e: PointerEvent) => {
        if (!jawDragState || !onJawDragRef.current) return;

        const cfg = jawDragRef.current;
        const dx = e.clientX - jawDragState.startX;
        const dy = e.clientY - jawDragState.startY;
        const dist = camera.position.distanceTo(controls.target);
        const translateSens = cfg?.translateSensitivity ?? 0.00035;
        const rotateSens = cfg?.rotateSensitivity ?? 0.04;
        const tScale = dist * translateSens;
        const rScale = rotateSens;

        let next: JawTransform = { ...jawDragState.startTransform };

        if (jawDragState.mode === "tilt") {
          next = {
            ...next,
            rx: Number((jawDragState.startTransform.rx - dy * rScale).toFixed(3)),
            ry: Number((jawDragState.startTransform.ry + dx * rScale).toFixed(3)),
          };
        } else if (jawDragState.mode === "roll") {
          next = {
            ...next,
            rz: Number((jawDragState.startTransform.rz + dx * rScale).toFixed(3)),
          };
        } else {
          const right = new THREE.Vector3();
          const up = new THREE.Vector3();
          camera.matrixWorld.extractBasis(right, up, new THREE.Vector3());

          const delta = new THREE.Vector3()
            .addScaledVector(right, dx * tScale)
            .addScaledVector(up, -dy * tScale);

          next = {
            ...next,
            tx: Number((jawDragState.startTransform.tx + delta.x).toFixed(4)),
            ty: Number((jawDragState.startTransform.ty + delta.y).toFixed(4)),
            tz: Number((jawDragState.startTransform.tz + delta.z).toFixed(4)),
          };
        }

        jawDragState.latestTransform = next;
        onJawDragRef.current(next);
      };

      const scheduleWheelEnd = (transform: JawTransform) => {
        if (wheelEndTimer) clearTimeout(wheelEndTimer);
        wheelEndTimer = setTimeout(() => {
          onJawDragEndRef.current?.(transform);
          wheelEndTimer = null;
        }, 320);
      };

      const onJawWheel = (e: WheelEvent) => {
        const cfg = jawDragRef.current;
        if (!cfg?.enabled || paintModeActive() || !onJawDragRef.current) return;
        // Plain wheel always zooms the camera; Alt+wheel over the jaw closes/opens it.
        if (!e.altKey) return;

        const movable = getMovableMesh();
        if (!movable) return;

        pointerToNdc(e);
        raycaster.setFromCamera(pointer, camera);
        const hits = raycaster.intersectObject(movable, false);
        if (!hits.length) return;

        e.preventDefault();
        e.stopImmediatePropagation();
        const wheelSens = 0.0001;
        const next: JawTransform = {
          ...cfg.transform,
          tz: Number((cfg.transform.tz - e.deltaY * wheelSens).toFixed(4)),
        };
        onJawDragRef.current(next);
        scheduleWheelEnd(next);
      };

      const endJawDrag = (e: PointerEvent) => {
        if (!jawDragState) return;
        controls.enabled = true;
        syncMouseButtons();
        onJawDragEndRef.current?.(jawDragState.latestTransform);
        jawDragState = null;
        if (renderer.domElement.hasPointerCapture(e.pointerId)) {
          renderer.domElement.releasePointerCapture(e.pointerId);
        }
      };

      const onJawPointerUp = (e: PointerEvent) => {
        if (!jawDragState) return;
        endJawDrag(e);
      };

      const onJawPointerCancel = (e: PointerEvent) => {
        if (!jawDragState) return;
        endJawDrag(e);
      };

      renderer.domElement.addEventListener("pointerdown", onJawPointerDown);
      renderer.domElement.addEventListener("pointermove", onJawPointerMove);
      renderer.domElement.addEventListener("pointerup", onJawPointerUp);
      renderer.domElement.addEventListener("pointercancel", onJawPointerCancel);
      // Capture phase so it runs before OrbitControls' wheel zoom and can swallow the event.
      renderer.domElement.addEventListener("wheel", onJawWheel, { passive: false, capture: true });

      const applyTransformToGroup = (g: THREE.Group, t: JawTransform) => {
        g.position.set(t.tx, t.ty, t.tz);
        g.rotation.set(
          THREE.MathUtils.degToRad(t.rx),
          THREE.MathUtils.degToRad(t.ry),
          THREE.MathUtils.degToRad(t.rz)
        );
        g.updateMatrixWorld(true);
      };

      const fitOverview = (extraPull = 1.7) => {
        const box = new THREE.Box3().setFromObject(root);
        markersGroup.children.forEach((child) => {
          if (child instanceof THREE.Mesh) box.expandByObject(child);
        });
        if (box.isEmpty()) return;
        modelCenter = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        modelRadius = Math.max(size.x, size.y, size.z) * 0.55 || 40;
        controls.target.copy(modelCenter);
        grid.position.set(modelCenter.x, modelCenter.y, box.min.z - 1);
        const dist = modelRadius * extraPull;
        camera.position.set(
          modelCenter.x + dist * 0.5,
          modelCenter.y + dist * 1.2,
          modelCenter.z + dist * 0.45
        );
        camera.near = Math.max(0.02, modelRadius / 300);
        camera.far = modelRadius * 80;
        camera.updateProjectionMatrix();
        controls.update();
      };

      const clearMeshes = () => {
        [upperMesh, lowerMesh, biteMesh].forEach((m) => {
          if (!m) return;
          m.parent?.remove(m);
          (m.material as THREE.Material).dispose();
        });
        upperMesh = lowerMesh = biteMesh = null;
      };

      const clearMarkers = () => {
        while (markersGroup.children.length) {
          const child = markersGroup.children[0];
          markersGroup.remove(child);
          if (child instanceof THREE.ArrowHelper) {
            child.line.geometry.dispose();
            (child.line.material as THREE.Material).dispose();
            child.cone.geometry.dispose();
            (child.cone.material as THREE.Material).dispose();
          } else if (
            child instanceof THREE.Mesh ||
            child instanceof THREE.Line ||
            child instanceof THREE.Sprite
          ) {
            if ("geometry" in child && child.geometry) child.geometry.dispose();
            const mat = child.material;
            if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
            else {
              if ((mat as THREE.SpriteMaterial).map) {
                (mat as THREE.SpriteMaterial).map?.dispose();
              }
              (mat as THREE.Material).dispose();
            }
          }
        }
      };

      const clearExclusionMarkers = () => {
        while (exclusionGroup.children.length) {
          const child = exclusionGroup.children[0];
          exclusionGroup.remove(child);
          if (child instanceof THREE.Mesh) {
            child.geometry.dispose();
            (child.material as THREE.Material).dispose();
          }
        }
      };

      const clearPriorityMarkers = () => {
        while (priorityGroup.children.length) {
          const child = priorityGroup.children[0];
          priorityGroup.remove(child);
          if (child instanceof THREE.Mesh) {
            child.geometry.dispose();
            (child.material as THREE.Material).dispose();
          }
        }
      };

      const rebuildRegionMarkers = (
        group: THREE.Group,
        regions: ExclusionRegion[],
        color: number,
        emissive: number,
        ringColor: number
      ) => {
        while (group.children.length) {
          const child = group.children[0];
          group.remove(child);
          if (child instanceof THREE.Mesh) {
            child.geometry.dispose();
            (child.material as THREE.Material).dispose();
          }
        }
        const markerScale = Math.max(modelRadius * 0.015, 0.45);
        regions.forEach((region) => {
          const radius = Math.max(region.radiusMm, markerScale * 0.35);
          const sphere = new THREE.Mesh(
            new THREE.SphereGeometry(radius, 20, 20),
            new THREE.MeshStandardMaterial({
              color,
              emissive,
              emissiveIntensity: 0.4,
              transparent: true,
              opacity: 0.28,
              depthWrite: false,
              side: THREE.DoubleSide,
            })
          );
          sphere.position.set(region.center.x, region.center.y, region.center.z);
          group.add(sphere);

          const ring = new THREE.Mesh(
            new THREE.RingGeometry(radius * 0.92, radius * 1.05, 32),
            new THREE.MeshBasicMaterial({
              color: ringColor,
              transparent: true,
              opacity: 0.65,
              side: THREE.DoubleSide,
              depthTest: false,
            })
          );
          ring.position.copy(sphere.position);
          ring.lookAt(camera.position);
          group.add(ring);
        });
      };

      const rebuildExclusionMarkers = (regions: ExclusionRegion[]) => {
        rebuildRegionMarkers(exclusionGroup, regions, 0xa855f7, 0x7c3aed, 0xc084fc);
      };

      const rebuildPriorityMarkers = (regions: ExclusionRegion[]) => {
        rebuildRegionMarkers(priorityGroup, regions, 0xf59e0b, 0xd97706, 0xfbbf24);
      };

      const makeLabelSprite = (text: string, color: string) => {
        const canvas = document.createElement("canvas");
        canvas.width = 256;
        canvas.height = 96;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, 256, 96);
          const x = 12;
          const y = 20;
          const w = 232;
          const h = 56;
          const r = 14;
          ctx.beginPath();
          ctx.moveTo(x + r, y);
          ctx.arcTo(x + w, y, x + w, y + h, r);
          ctx.arcTo(x + w, y + h, x, y + h, r);
          ctx.arcTo(x, y + h, x, y, r);
          ctx.arcTo(x, y, x + w, y, r);
          ctx.closePath();
          ctx.fillStyle = "rgba(8, 12, 24, 0.92)";
          ctx.fill();
          ctx.strokeStyle = color;
          ctx.lineWidth = 3;
          ctx.stroke();
          ctx.fillStyle = "#f8fafc";
          ctx.font = "700 34px Inter, system-ui, sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(text, 128, 50);
        }
        const tex = new THREE.CanvasTexture(canvas);
        tex.colorSpace = THREE.SRGBColorSpace;
        const mat = new THREE.SpriteMaterial({
          map: tex,
          transparent: true,
          depthTest: false,
        });
        const sprite = new THREE.Sprite(mat);
        sprite.scale.set(5.2, 1.95, 1);
        return sprite;
      };

      const getJawOrigin = (jaw: "upper" | "lower") => {
        const group = jaw === "upper" ? upperGroup : lowerGroup;
        const box = new THREE.Box3().setFromObject(group);
        if (box.isEmpty()) return new THREE.Vector3();
        return box.getCenter(new THREE.Vector3());
      };

      const getJawRotation = (jaw: "upper" | "lower") => {
        const group = jaw === "upper" ? upperGroup : lowerGroup;
        const quat = new THREE.Quaternion();
        group.getWorldQuaternion(quat);
        return quat;
      };

      const rebuildAdjustmentMarkers = (
        adjustments: ContactAdjustment[],
        selectedId: number | null,
        showAll: boolean,
        jaw: "upper" | "lower"
      ) => {
        clearMarkers();
        if (!adjustments.length) return;

        const origin = getJawOrigin(jaw);
        const jawRotation = getJawRotation(jaw);
        const arrowLen = Math.max(modelRadius * 0.22, 6);

        adjustments.forEach((adj, index) => {
          const isSelected = adj.id === selectedId;
          if (!showAll && !isSelected) return;

          const dir = AXIS_DIRECTION[adj.axis]
            .clone()
            .multiplyScalar(adj.direction)
            .applyQuaternion(jawRotation)
            .normalize();

          const color = MARKER_COLORS[index % MARKER_COLORS.length];
          const arrow = new THREE.ArrowHelper(
            dir,
            origin,
            arrowLen * (isSelected ? 1.25 : 0.9),
            color,
            arrowLen * 0.18,
            arrowLen * 0.12
          );
          markersGroup.add(arrow);

          const sprite = makeLabelSprite(`#${adj.id}`, "#22d3ee");
          sprite.position.copy(origin).add(dir.clone().multiplyScalar(arrowLen * 1.2));
          markersGroup.add(sprite);
        });
      };

      const setMeshes = (m: OcclusionViewerMeshes) => {
        clearMeshes();
        if (m.upper) {
          const mat = new THREE.MeshStandardMaterial({
            vertexColors: Boolean(m.upper.getAttribute("color")),
            color: m.upper.getAttribute("color") ? 0xffffff : 0xfca5a5,
            metalness: 0.03,
            roughness: 0.36,
            side: THREE.DoubleSide,
          });
          upperMesh = new THREE.Mesh(m.upper, mat);
          upperGroup.add(upperMesh);
        }
        if (m.lower) {
          const hasVertexColors = Boolean(m.lower.getAttribute("color"));
          const mat = new THREE.MeshStandardMaterial({
            vertexColors: hasVertexColors,
            color: hasVertexColors ? 0xffffff : 0xe2e8f0,
            metalness: 0.05,
            roughness: 0.42,
            side: THREE.DoubleSide,
            transparent: !hasVertexColors,
            opacity: hasVertexColors ? 1 : 0.48,
          });
          lowerMesh = new THREE.Mesh(m.lower, mat);
          lowerGroup.add(lowerMesh);
        }
        if (m.bite && m.showBite) {
          const mat = new THREE.MeshStandardMaterial({
            color: 0x6ee7b7,
            metalness: 0.05,
            roughness: 0.5,
            transparent: true,
            opacity: 0.18,
            side: THREE.DoubleSide,
          });
          biteMesh = new THREE.Mesh(m.bite, mat);
          biteGroup.add(biteMesh);
        }
        if (!m.upper && !m.lower) {
          hasFramed = false;
        } else if (!hasFramed) {
          fitOverview(1.55);
          hasFramed = true;
        }
      };

      let raf = 0;
      const tick = () => {
        raf = requestAnimationFrame(tick);
        controls.update();
        renderer.render(scene, camera);
      };
      tick();

      const onResize = () => {
        const w = mount.clientWidth;
        const h = Math.max(1, mount.clientHeight);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      };
      const ro = new ResizeObserver(onResize);
      ro.observe(mount);

      apiRef.current = {
        setMeshes,
        setJawTransforms: (u, l) => {
          applyTransformToGroup(upperGroup, u);
          applyTransformToGroup(lowerGroup, l);
        },
        setContactAdjustments: (adjustments, selectedId, showAll, jaw) => {
          rebuildAdjustmentMarkers(adjustments, selectedId, showAll, jaw);
        },
        setExclusionRegions: (regions) => {
          rebuildExclusionMarkers(regions);
        },
        setPriorityRegions: (regions) => {
          rebuildPriorityMarkers(regions);
        },
        setExcludeMode: (active) => {
          setExcludeMode(active);
        },
        setPriorityPaintMode: (active) => {
          setPriorityPaintMode(active);
        },
        setJawDragMode: (config) => {
          setJawDragMode(config);
        },
        zoomOutOverview: () => fitOverview(1.9),
        dispose: () => {
          cancelAnimationFrame(raf);
          ro.disconnect();
          if (paintHandler) {
            renderer.domElement.removeEventListener("pointerdown", paintHandler);
          }
          renderer.domElement.removeEventListener("pointermove", onPointerMove);
          renderer.domElement.removeEventListener("pointerdown", onJawPointerDown);
          renderer.domElement.removeEventListener("pointermove", onJawPointerMove);
          renderer.domElement.removeEventListener("pointerup", onJawPointerUp);
          renderer.domElement.removeEventListener("pointercancel", onJawPointerCancel);
          renderer.domElement.removeEventListener("wheel", onJawWheel, { capture: true });
          if (wheelEndTimer) clearTimeout(wheelEndTimer);
          clearMeshes();
          clearMarkers();
          clearExclusionMarkers();
          clearPriorityMarkers();
          controls.dispose();
          renderer.dispose();
          if (renderer.domElement.parentElement === mount) {
            mount.removeChild(renderer.domElement);
          }
        },
      };

      setMeshes(meshes);
      applyTransformToGroup(upperGroup, upperTransform);
      applyTransformToGroup(lowerGroup, lowerTransform);
      rebuildAdjustmentMarkers(
        contactAdjustments,
        selectedAdjustmentId,
        showAllAdjustments,
        movableJaw
      );
      rebuildExclusionMarkers(exclusionRegions);
      rebuildPriorityMarkers(priorityRegions);
      setExcludeMode(excludeMode);
      setPriorityPaintMode(priorityPaintMode);
      setJawDragMode(jawDrag);

      return () => {
        apiRef.current?.dispose();
        apiRef.current = null;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      apiRef.current?.setMeshes(meshes);
    }, [meshes]);

    useEffect(() => {
      apiRef.current?.setJawTransforms(upperTransform, lowerTransform);
    }, [upperTransform, lowerTransform]);

    useEffect(() => {
      apiRef.current?.setContactAdjustments(
        contactAdjustments,
        selectedAdjustmentId,
        showAllAdjustments,
        movableJaw
      );
    }, [
      contactAdjustments,
      selectedAdjustmentId,
      showAllAdjustments,
      movableJaw,
      upperTransform,
      lowerTransform,
    ]);

    useEffect(() => {
      apiRef.current?.setExclusionRegions(exclusionRegions);
    }, [exclusionRegions]);

    useEffect(() => {
      apiRef.current?.setPriorityRegions(priorityRegions);
    }, [priorityRegions]);

    useEffect(() => {
      apiRef.current?.setExcludeMode(excludeMode);
    }, [excludeMode]);

    useEffect(() => {
      apiRef.current?.setPriorityPaintMode(priorityPaintMode);
    }, [priorityPaintMode]);

    useEffect(() => {
      apiRef.current?.setJawDragMode(jawDrag);
    }, [jawDrag]);

    return (
      <div className={`relative ${className ?? ""}`}>
        <div ref={mountRef} className="absolute inset-0 h-full w-full" />
        {caption ? (
          <div
            className={
              captionClassName ??
              "pointer-events-none absolute left-3 top-3 z-10 rounded-lg border border-white/15 bg-[#0b1220]/90 px-2.5 py-1 text-[11px] font-semibold text-slate-200 shadow-lg backdrop-blur-sm"
            }
          >
            {caption}
          </div>
        ) : null}
      </div>
    );
  }
);

OcclusionViewer.displayName = "OcclusionViewer";

export default OcclusionViewer;
